// middleware action creators
import * as ContractIAImportStore from '../../../redux/middleware/ContractIAImportMiddleware';
import * as ContractAddendumStore from '../../../redux/middleware/ContractAddendum.Middleware';
import * as ContractStagesStore from '../../../redux/middleware/ContractStageMiddleware';
import * as ContractConceptStore from '../../../redux/middleware/ContractConceptMiddleware';
import { useEffect, useRef } from 'react';

// state
import { AppState } from '../../../redux/reducers';
import { ContractIAImportProcess } from '../../../models/Entities/Contracts/ContractIAImportProcess';
import { ErrorType } from '../../../models/HttpError';
import { Contract } from '../../../models/Entities/Contracts/Contract';

//#region Props

export interface IProps {
	contractId: number;
}

interface ConnectedProps {
	// get last
	isGettingLast: boolean;
	successGettingLast: boolean | undefined;
	failureGettingLast: boolean;
	errorGettingLast: ErrorType | undefined;

	// create
	isCreating: boolean;
	successCreating: boolean | undefined;
	failureCreating: boolean;
	errorCreating: ErrorType | undefined;

	// history
	isGettingHistory: boolean;
	failureGettingHistory: boolean;
	history: ContractIAImportProcess[];

	lastProcess: ContractIAImportProcess | null;
	contract: Contract | undefined;
}

interface DispatchProps {
	CreateImportContractProcess: typeof ContractIAImportStore.actionCreators.CreateImportContractProcess;
	GetLastImportContractProcess: typeof ContractIAImportStore.actionCreators.GetLastImportContractProcess;
	GetImportContractProcessHistory: typeof ContractIAImportStore.actionCreators.GetImportContractProcessHistory;
	GetContractAddendumsByContractId: typeof ContractAddendumStore.actionCreators.GetContractAddendumsByContractId;
	GetAllContractStagesByAddendumId: typeof ContractStagesStore.actionCreators.GetAllContractStagesByAddendumId;
	GetAllContractsConceptByAddendum: typeof ContractConceptStore.actionCreators.GetAllContractsConceptByAddendum;
	
}

export type Props = IProps & ConnectedProps & DispatchProps;

export const mapStateToProps = (state: AppState, ownProps: IProps) => {
	// Obtener el contrato específico del estado
	const contract = state.Contract?.list?.find(c => c.id === ownProps.contractId);

	// The IA import slice keeps a single process and a single history for the
	// whole app. Switching contracts must not surface the previous one's data,
	// so ownership is verified here instead of being trusted from the store.
	const storedProcess = state.ContractIAImport?.lastProcess ?? null;
	const ownedProcess =
		storedProcess && storedProcess.contractID === ownProps.contractId ? storedProcess : null;

	const ownedHistory = (state.ContractIAImport?.history ?? []).filter(
		(process: ContractIAImportProcess) => process.contractID === ownProps.contractId
	);
	
	return {
		// last
		isGettingLast: state.ContractIAImport?.isGettingLastProcess || false,
		successGettingLast: state.ContractIAImport?.successGettingLastProcess,
		failureGettingLast: state.ContractIAImport?.failOnGettingLastProcess || false,
		errorGettingLast: state.ContractIAImport?.error,

		// create
		isCreating: state.ContractIAImport?.isCreatingProcess || false,
		successCreating: state.ContractIAImport?.successCreatingProcess,
		failureCreating: state.ContractIAImport?.failOnCreatingProcess || false,
		errorCreating: state.ContractIAImport?.error,

		// Leer lastProcess del estado dedicado ContractIAImport (el reducer actualiza este slice,
		// no el objeto contrato en Contract.list)
		// history
		isGettingHistory: state.ContractIAImport?.isGettingHistory || false,
		failureGettingHistory: state.ContractIAImport?.failOnGettingHistory || false,
		history: ownedHistory,

		lastProcess: ownedProcess,
		contract: contract,
	} as ConnectedProps;
};

export const mapDispatchToProps = {
	...ContractIAImportStore.actionCreators,
	...ContractAddendumStore.actionCreators,
	...ContractStagesStore.actionCreators,
	...ContractConceptStore.actionCreators,
}

// When IA import process finishes, refresh stages and concepts by addendum
export const useIAImportCompletionRefresh = (
	contractId: number,
	lastProcess: ContractIAImportProcess | null,
	GetAllContractStagesByAddendumId: DispatchProps['GetAllContractStagesByAddendumId'],
	GetAllContractsConceptByAddendum: DispatchProps['GetAllContractsConceptByAddendum'],
	GetContractAddendumsByContractId: DispatchProps['GetContractAddendumsByContractId']
) => {
	const lastProcessIdRef = useRef<string | undefined>(undefined);
	const lastStatusRef = useRef<number | undefined>(undefined);
	const hasRefreshedRef = useRef<boolean>(false);

	useEffect(() => {
		// No hacer nada si no hay proceso
		if (!lastProcess) {
			// Resetear las referencias cuando no hay proceso
			lastProcessIdRef.current = undefined;
			lastStatusRef.current = undefined;
			hasRefreshedRef.current = false;
			return;
		}
		
		// Verificar si realmente cambiaron los VALORES (no solo la referencia del objeto)
		const processIdChanged = lastProcess.id !== lastProcessIdRef.current;
		const statusChanged = lastProcess.status !== lastStatusRef.current;
		
		// Si no cambió ni el id ni el status, solo cambió la referencia del objeto - no hacer nada
		if (!processIdChanged && !statusChanged) {
			return;
		}
		
		// Si cambió el proceso, resetear el flag de refresco
		if (processIdChanged) {
			hasRefreshedRef.current = false;
		}

		// Solo refrescar cuando cambia de estado a finalizado (3)
		const isCompleted = lastProcess.status === 3;
		const previousStatus = lastStatusRef.current;
		const statusChangedToCompleted = previousStatus !== 3 && isCompleted;

		// Refrescar solo UNA VEZ cuando el estado cambia a completado (3)
		if (statusChangedToCompleted && !hasRefreshedRef.current) {
			console.log('Refreshing addendums, stages and concepts after IA import completion', {
				processId: lastProcess.id,
				previousStatus,
				currentStatus: lastProcess.status,
				contractId
			});
			
			hasRefreshedRef.current = true;
			GetContractAddendumsByContractId(contractId);
		}
		
		// Actualizar las referencias DESPUÉS de las validaciones
		if (lastProcess.id) {
			lastProcessIdRef.current = lastProcess.id;
		}
		if (lastProcess.status !== undefined) {
			lastStatusRef.current = lastProcess.status;
		}
	}, [contractId, lastProcess, GetContractAddendumsByContractId]);
};


